import { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Table, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";

import BackButton from "../../Components/BackButton";
import Loader from "../../Components/Loader";
import Message from "../../Components/Message";

import {
  useAdminGetCatalogMakesQuery,
  useAdminListModelsQuery,
  useAdminCreateModelMutation,
  useAdminUpdateModelMutation,
  useAdminDeleteModelMutation,
  useAdminListYearsQuery,
  useAdminCreateYearMutation,
  useAdminUpdateYearMutation,
  useAdminDeleteYearMutation,
  useAdminListTrimsQuery,
  useAdminCreateTrimMutation,
  useAdminUpdateTrimMutation,
  useAdminDeleteTrimMutation,
  useAdminListEnginesQuery,
  useAdminCreateEngineMutation,
  useAdminUpdateEngineMutation,
  useAdminDeleteEngineMutation,
  useAdminUpsertFitmentMutation,
  useAdminBulkUpsertFitmentsMutation,
} from "../../slices/productsApiSlice";

export default function FitmentCatalogAdminScreen() {
  /* -------------------- Makes -------------------- */
  const {
    data: makes = [],
    isLoading: loadingMakes,
    error: makesError,
  } = useAdminGetCatalogMakesQuery();

  const activeMakes = useMemo(
    () => makes.filter((m) => m.active !== false),
    [makes]
  );

  const [make, setMake] = useState("");

  /* -------------------- Models ------------------- */
  const {
    data: models = [],
    refetch: refetchModels,
    isLoading: loadingModels,
    error: modelsError,
  } = useAdminListModelsQuery({ make }, { skip: !make });

  const [model, setModel] = useState("");

  useEffect(() => {
    setModel("");
  }, [make]);

  /* -------------------- Years -------------------- */
  const {
    data: years = [],
    refetch: refetchYears,
    isLoading: loadingYears,
    error: yearsError,
    isUninitialized: yearsUninit,
  } = useAdminListYearsQuery({ make, model }, { skip: !(make && model) });

  const [year, setYear] = useState("");

  useEffect(() => {
    setYear("");
  }, [model]);

  /* -------------------- Trims -------------------- */
  const {
    data: trims = [],
    refetch: refetchTrims,
    isLoading: loadingTrims,
    error: trimsError,
    isUninitialized: trimsUninit,
  } = useAdminListTrimsQuery(
    { make, model, year },
    { skip: !(make && model && year) }
  );

  /* ------------------- Engines ------------------- */
  const {
    data: engines = [],
    refetch: refetchEngines,
    isLoading: loadingEngines,
    error: enginesError,
  } = useAdminListEnginesQuery(
    { make, model, year },
    { skip: !(make && model && year) }
  );

  /* ------------------ Mutations ------------------ */
  const [createModel, { isLoading: cModel }] = useAdminCreateModelMutation();
  const [updateModel] = useAdminUpdateModelMutation();
  const [deleteModel] = useAdminDeleteModelMutation();

  const [createYear, { isLoading: cYear }] = useAdminCreateYearMutation();
  const [updateYear] = useAdminUpdateYearMutation();
  const [deleteYear] = useAdminDeleteYearMutation();

  const [createTrim, { isLoading: cTrim }] = useAdminCreateTrimMutation();
  const [updateTrim] = useAdminUpdateTrimMutation();
  const [deleteTrim] = useAdminDeleteTrimMutation();

  const [createEngine, { isLoading: cEngine }] =
    useAdminCreateEngineMutation();
  const [updateEngine] = useAdminUpdateEngineMutation();
  const [deleteEngine] = useAdminDeleteEngineMutation();

  /* ------------------ Add forms ------------------ */
  const [newModel, setNewModel] = useState({ model: "", sort: 0, active: true });
  const [newYear, setNewYear] = useState({ year: "", sort: 0, active: true });
  const [newTrim, setNewTrim] = useState({ trim: "", sort: 0, active: true });
  const [newEngine, setNewEngine] = useState({
    engine: "",
    sort: 0,
    active: true,
  });

  const confirmDelete = (itemLabel) =>
    window.confirm(
      `Are you sure you want to delete ${itemLabel}? This cannot be undone.`
    );

  const onDeleteModel = async (id, label) => {
    if (!confirmDelete(`model "${label}"`)) return;

    try {
      await deleteModel(id).unwrap();
      toast.success("Model deleted");
      refetchModels();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to delete model");
    }
  };

  const onDeleteYear = async (id, label) => {
    if (!confirmDelete(`year "${label}"`)) return;

    try {
      await deleteYear(id).unwrap();
      toast.success("Year deleted");
      refetchYears();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to delete year");
    }
  };

  const onDeleteTrim = async (id, label) => {
    if (!confirmDelete(`trim "${label}"`)) return;

    try {
      await deleteTrim(id).unwrap();
      toast.success("Trim deleted");
      refetchTrims();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to delete trim");
    }
  };

  const onDeleteEngine = async (id, label) => {
    if (!confirmDelete(`engine "${label}"`)) return;

    try {
      await deleteEngine(id).unwrap();
      toast.success("Engine deleted");
      refetchEngines();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to delete engine");
    }
  };

  const onAddModel = async () => {
    if (!make || !newModel.model.trim()) return;

    try {
      await createModel({
        make,
        model: newModel.model.trim(),
        sort: Number(newModel.sort) || 0,
        active: !!newModel.active,
      }).unwrap();

      setNewModel({ model: "", sort: 0, active: true });
      refetchModels();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to add model");
    }
  };

  const onAddYear = async () => {
    if (!(make && model) || !String(newYear.year).trim()) return;

    try {
      await createYear({
        make,
        model,
        year: Number(newYear.year),
        sort: Number(newYear.sort) || 0,
        active: !!newYear.active,
      }).unwrap();

      setNewYear({ year: "", sort: 0, active: true });
      refetchYears();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to add year");
    }
  };

  const onAddTrim = async () => {
    if (!(make && model && year)) return;

    const t = String(newTrim.trim || "").trim();
    if (!t) return;

    try {
      await createTrim({
        make,
        model,
        year: Number(year),
        trim: t,
        sort: Number(newTrim.sort) || 0,
        active: !!newTrim.active,
      }).unwrap();

      setNewTrim({ trim: "", sort: 0, active: true });
      refetchTrims();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to add trim");
    }
  };

  const onAddEngine = async () => {
    if (!(make && model && year)) return;

    const e = String(newEngine.engine || "").trim();
    if (!e) return;

    try {
      await createEngine({
        make,
        model,
        year: Number(year),
        engine: e,
        sort: Number(newEngine.sort) || 0,
        active: !!newEngine.active,
      }).unwrap();

      setNewEngine({ engine: "", sort: 0, active: true });
      refetchEngines();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to add engine");
    }
  };

  /* ------------------ Quick add ------------------ */
  const [upsertFitment, { isLoading: quickSaving }] =
    useAdminUpsertFitmentMutation();

  const [bulkUpsert, { isLoading: bulkSaving }] =
    useAdminBulkUpsertFitmentsMutation();

  const [quick, setQuick] = useState({ model: "", year: "", trim: "" });
  const [bulkText, setBulkText] = useState("");

  const onQuickAdd = async () => {
    if (!make || !quick.model.trim()) {
      toast.error("Make and model are required");
      return;
    }

    try {
      await upsertFitment({
        make,
        model: quick.model.trim(),
        year: quick.year ? Number(quick.year) : undefined,
        trim: quick.trim?.trim() || undefined,
      }).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Quick add failed");
      return;
    }

    setQuick({ model: "", year: "", trim: "" });
    refetchModels();
    if (!yearsUninit) refetchYears();
    if (!trimsUninit) refetchTrims();
  };

  const onBulkImport = async () => {
    if (!bulkText.trim()) return;

    try {
      const result = await bulkUpsert({ text: bulkText }).unwrap();
      const total = Number(result?.count ?? 0);
      const skipped = Number(result?.skipped ?? 0);
      const added = Math.max(0, total - skipped);
      const summary = total
        ? `Processed ${total} line${total === 1 ? "" : "s"}`
        : "Bulk import completed";
      toast.success(`${summary}. Added ${added}, skipped ${skipped}.`);
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Bulk import failed");
      return;
    }

    setBulkText("");
    if (make) refetchModels();
    if (!yearsUninit) refetchYears();
    if (!trimsUninit) refetchTrims();
  };

  return (
    <>
      <BackButton className="mb-3" fallback="/admin/makes" />
      <h1>Vehicle Catalog</h1>

      {/* ---------------- Make selector ---------------- */}
      <Card className="mb-3">
        <Card.Body>
          {loadingMakes && <Loader />}
          {makesError && (
            <Message variant="danger">
              {makesError?.data?.message || makesError.error}
            </Message>
          )}

          <Form.Label>Make</Form.Label>
          <Form.Select
            value={make}
            onChange={(e) => setMake(e.target.value)}
            disabled={!activeMakes.length}
          >
            <option value="">Select a make</option>
            {activeMakes.map((m) => (
              <option key={m._id} value={m.make}>
                {m.make}
              </option>
            ))}
          </Form.Select>
        </Card.Body>
      </Card>

      {/* ---------------- Quick add (one row) ---------------- */}
      <Card className="mb-3">
        <Card.Header className="fw-semibold">Quick add (one row)</Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-center">
            <Col md={3}>
              <Form.Label className="small text-muted m-0">Make</Form.Label>
              <Form.Control value={make} readOnly />
            </Col>

            <Col md={3}>
              <Form.Label className="small text-muted m-0">Model</Form.Label>
              <Form.Control
                placeholder="e.g., Rav4"
                value={quick.model}
                onChange={(e) =>
                  setQuick((s) => ({ ...s, model: e.target.value }))
                }
                list="model-suggestions"
              />
              <datalist id="model-suggestions">
                {models.map((m) => (
                  <option key={m._id} value={m.model} />
                ))}
              </datalist>
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted m-0">Year</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g., 2020"
                value={quick.year}
                onChange={(e) =>
                  setQuick((s) => ({ ...s, year: e.target.value }))
                }
              />
            </Col>

            <Col md={3}>
              <Form.Label className="small text-muted m-0">
                Trim (optional)
              </Form.Label>
              <Form.Control
                placeholder="e.g., XSE / Sport"
                value={quick.trim}
                onChange={(e) =>
                  setQuick((s) => ({ ...s, trim: e.target.value }))
                }
              />
            </Col>

            <Col md={1} className="d-grid">
              <Form.Label className="invisible">_</Form.Label>
              <Button
                onClick={onQuickAdd}
                disabled={quickSaving || !make || !quick.model.trim()}
              >
                Add
              </Button>
            </Col>
          </Row>

          <div className="small text-muted mt-2">
            Tip: Type a model name and press <kbd>Enter</kbd> to add; year/trim
            are optional.
          </div>
        </Card.Body>
      </Card>

      {/* ---------------- Bulk paste ---------------- */}
      <Card className="mb-4">
        <Card.Header className="fw-semibold">Bulk import</Card.Header>
        <Card.Body>
          <Form.Text className="d-block mb-2">
            Paste one per line: <code>Make,Model,Year,Trim</code> (Year/Trim
            optional)
          </Form.Text>

          <Form.Control
            as="textarea"
            rows={4}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />

          <div className="mt-2 d-flex gap-2">
            <Button
              onClick={onBulkImport}
              disabled={bulkSaving || !bulkText.trim()}
            >
              Import
            </Button>
            <Button variant="light" onClick={() => setBulkText("")}>
              Clear
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        {/* ---------------- Models ---------------- */}
        <Col md={3}>
          <Card>
            <Card.Header className="fw-semibold">Models</Card.Header>
            <Card.Body>
              {loadingModels && <Loader />}
              {modelsError && (
                <Message variant="danger">
                  {modelsError?.data?.message || modelsError.error}
                </Message>
              )}

              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  placeholder="New model…"
                  style={{ width: "48%" }}
                  value={newModel.model}
                  onChange={(e) =>
                    setNewModel((s) => ({ ...s, model: e.target.value }))
                  }
                />

                <Form.Check
                  type="switch"
                  checked={newModel.active}
                  onChange={(e) =>
                    setNewModel((s) => ({ ...s, active: e.target.checked }))
                  }
                />

                <Form.Control
                  type="number"
                  style={{ width: "22%" }}
                  value={newModel.sort}
                  onChange={(e) =>
                    setNewModel((s) => ({ ...s, sort: e.target.value }))
                  }
                />

                <Button
                  onClick={onAddModel}
                  disabled={!newModel.model.trim() || cModel}
                >
                  Add
                </Button>
              </div>

              <Table striped bordered hover size="sm" className="align-middle">
                <tbody>
                  {models.map((m) => (
                    <tr
                      key={m._id}
                      className={m.model === model ? "table-active" : ""}
                      onClick={() => setModel(m.model)}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ width: "45%" }}>
                        <Form.Control
                          defaultValue={m.model}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== m.model)
                              updateModel({ id: m._id, model: v });
                          }}
                        />
                      </td>

                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          defaultChecked={!!m.active}
                          onChange={(e) =>
                            updateModel({ id: m._id, active: e.target.checked })
                          }
                        />
                      </td>

                      <td style={{ width: "22%" }}>
                        <Form.Control
                          type="number"
                          defaultValue={m.sort || 0}
                          onBlur={(e) =>
                            updateModel({
                              id: m._id,
                              sort: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </td>

                      <td className="text-end" style={{ width: "5%" }}>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteModel(m._id, m.model);
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* ---------------- Years ---------------- */}
        <Col md={3}>
          <Card>
            <Card.Header className="fw-semibold">Years</Card.Header>
            <Card.Body>
              {loadingYears && <Loader />}
              {yearsError && (
                <Message variant="danger">
                  {yearsError?.data?.message || yearsError.error}
                </Message>
              )}

              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  placeholder="e.g., 2018"
                  style={{ width: "48%" }}
                  value={newYear.year}
                  onChange={(e) =>
                    setNewYear((s) => ({ ...s, year: e.target.value }))
                  }
                  disabled={!model}
                />

                <Form.Check
                  type="switch"
                  checked={newYear.active}
                  onChange={(e) =>
                    setNewYear((s) => ({ ...s, active: e.target.checked }))
                  }
                  disabled={!model}
                />

                <Form.Control
                  type="number"
                  style={{ width: "22%" }}
                  value={newYear.sort}
                  onChange={(e) =>
                    setNewYear((s) => ({ ...s, sort: e.target.value }))
                  }
                  disabled={!model}
                />

                <Button
                  onClick={onAddYear}
                  disabled={!model || !String(newYear.year).trim() || cYear}
                >
                  Add
                </Button>
              </div>

              <Table striped bordered hover size="sm" className="align-middle">
                <tbody>
                  {years.map((y) => (
                    <tr
                      key={y._id}
                      className={
                        String(y.year) === String(year) ? "table-active" : ""
                      }
                      onClick={() => setYear(y.year)}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ width: "45%" }}>
                        <Form.Control
                          type="number"
                          defaultValue={y.year}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (!Number.isNaN(v) && v !== y.year)
                              updateYear({ id: y._id, year: v });
                          }}
                        />
                      </td>

                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          defaultChecked={!!y.active}
                          onChange={(e) =>
                            updateYear({ id: y._id, active: e.target.checked })
                          }
                        />
                      </td>

                      <td style={{ width: "22%" }}>
                        <Form.Control
                          type="number"
                          defaultValue={y.sort || 0}
                          onBlur={(e) =>
                            updateYear({
                              id: y._id,
                              sort: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </td>

                      <td className="text-end" style={{ width: "5%" }}>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteYear(y._id, y.year);
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* ---------------- Trims ---------------- */}
        <Col md={3}>
          <Card>
            <Card.Header className="fw-semibold">
              Trims (select a Year first)
            </Card.Header>
            <Card.Body>
              {loadingTrims && <Loader />}
              {trimsError && (
                <Message variant="danger">
                  {trimsError?.data?.message || trimsError.error}
                </Message>
              )}

              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  placeholder="e.g., XLE / Sport"
                  style={{ width: "48%" }}
                  value={newTrim.trim}
                  onChange={(e) =>
                    setNewTrim((s) => ({ ...s, trim: e.target.value }))
                  }
                  disabled={!year}
                />

                <Form.Check
                  type="switch"
                  checked={newTrim.active}
                  onChange={(e) =>
                    setNewTrim((s) => ({ ...s, active: e.target.checked }))
                  }
                  disabled={!year}
                />

                <Form.Control
                  type="number"
                  style={{ width: "22%" }}
                  value={newTrim.sort}
                  onChange={(e) =>
                    setNewTrim((s) => ({ ...s, sort: e.target.value }))
                  }
                  disabled={!year}
                />

                <Button
                  onClick={onAddTrim}
                  disabled={!year || !newTrim.trim?.trim() || cTrim}
                >
                  Add
                </Button>
              </div>

              <Table striped bordered hover size="sm" className="align-middle">
                <tbody>
                  {trims.map((t) => (
                    <tr key={t._id}>
                      <td style={{ width: "45%" }}>
                        <Form.Control
                          defaultValue={t.trim}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== t.trim)
                              updateTrim({ id: t._id, trim: v });
                          }}
                        />
                      </td>

                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          defaultChecked={!!t.active}
                          onChange={(e) =>
                            updateTrim({ id: t._id, active: e.target.checked })
                          }
                        />
                      </td>

                      <td style={{ width: "22%" }}>
                        <Form.Control
                          type="number"
                          defaultValue={t.sort || 0}
                          onBlur={(e) =>
                            updateTrim({
                              id: t._id,
                              sort: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </td>

                      <td className="text-end" style={{ width: "5%" }}>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => onDeleteTrim(t._id, t.trim)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* ---------------- Engines ---------------- */}
        <Col md={3}>
          <Card>
            <Card.Header className="fw-semibold">Engines</Card.Header>
            <Card.Body>
              {loadingEngines && <Loader />}
              {enginesError && (
                <Message variant="danger">
                  {enginesError?.data?.message || enginesError.error}
                </Message>
              )}

              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  placeholder="e.g., 2.5L"
                  value={newEngine.engine}
                  style={{ width: "48%" }}
                  onChange={(e) =>
                    setNewEngine((s) => ({ ...s, engine: e.target.value }))
                  }
                  disabled={!year}
                />

                <Form.Check
                  type="switch"
                  checked={newEngine.active}
                  onChange={(e) =>
                    setNewEngine((s) => ({ ...s, active: e.target.checked }))
                  }
                  disabled={!year}
                />

                <Form.Control
                  type="number"
                  style={{ width: "22%" }}
                  value={newEngine.sort}
                  onChange={(e) =>
                    setNewEngine((s) => ({ ...s, sort: e.target.value }))
                  }
                  disabled={!year}
                />

                <Button
                  onClick={onAddEngine}
                  disabled={!year || !newEngine.engine.trim() || cEngine}
                >
                  Add
                </Button>
              </div>

              <Table striped bordered hover size="sm" className="align-middle">
                <tbody>
                  {engines.map((en) => (
                    <tr key={en._id}>
                      <td style={{ width: "45%" }}>
                        <Form.Control
                          defaultValue={en.engine}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== en.engine)
                              updateEngine({ id: en._id, engine: v });
                          }}
                        />
                      </td>

                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          defaultChecked={!!en.active}
                          onChange={(e) =>
                            updateEngine({
                              id: en._id,
                              active: e.target.checked,
                            })
                          }
                        />
                      </td>

                      <td style={{ width: "22%" }}>
                        <Form.Control
                          type="number"
                          defaultValue={en.sort || 0}
                          onBlur={(e) =>
                            updateEngine({
                              id: en._id,
                              sort: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </td>

                      <td className="text-end" style={{ width: "5%" }}>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => onDeleteEngine(en._id, en.engine)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
